(() => {
  if (window.pcdItchBnpl || window.ph_feature_flag_key === 'test-tranche-calm') return;

  const flag = 'test-tranche-itch';
  const itchHandle = 'allergy-relief-herbal-dog-treats-90-soft-chews';
  let cohort;
  let cart;
  let unitPrice;
  let unitProductId;
  let exposureCaptured = false;
  let sdkRequested = false;
  let sdkEventsBound = false;
  let infoModalOpen = false;
  let infoModalClosing = false;
  let infoModalTrigger;
  let observer;
  const visibleTargets = new Set();
  const targets = new Map();

  const panels = () => Array.from(document.querySelectorAll('[data-itch-bnpl]'));
  const hasItch = () => cart?.items?.some(item => item.handle === itchHandle) === true;
  const relevant = panel => panel.dataset.context !== 'cart' || hasItch();
  const activeSubscription = panel => {
    if (panel.dataset.context === 'cart') {
      return cart?.items?.some(item => Boolean(item.selling_plan_allocation)) === true;
    }
    const form = document.getElementById(panel.dataset.formId);
    const plan = form?.elements.namedItem('selling_plan');
    return Boolean(plan && !plan.disabled && plan.value);
  };

  const eligibleForKlarna = panel => {
    const amount = Number(panel.dataset.amount);
    const currency = panel.dataset.context === 'cart' ? cart?.currency : panel.dataset.currency;
    return relevant(panel) && Boolean(panel.querySelector('[data-itch-bnpl-klarna]')) &&
      !activeSubscription(panel) && panel.dataset.country === 'GB' && currency === 'GBP' &&
      Number.isSafeInteger(amount) && amount > 0;
  };

  const placementOpen = panel => {
    if (panel.dataset.context === 'modal') {
      return panel.closest('.pcd-modal-overlay')?.classList.contains('is-visible') === true;
    }
    if (panel.dataset.context === 'cart') {
      return panel.closest('#cart-notification')?.classList.contains('active') === true;
    }
    return true;
  };

  function bindSdkEvents() {
    const messaging = window.Klarna?.OnsiteMessaging;
    if (!messaging?.on || sdkEventsBound) return;
    sdkEventsBound = true;
    messaging.on('informational-modal-opened', () => {
      infoModalOpen = true;
      infoModalClosing = false;
      infoModalTrigger = document.activeElement;
      window.removeTrapFocus?.();
    });
    messaging.on('informational-modal-closed', () => {
      infoModalOpen = false;
      // The click closing Klarna can still be bubbling outside the cart.
      infoModalClosing = true;
      setTimeout(() => { infoModalClosing = false; }, 0);
      const drawer = document.getElementById('cart-notification');
      if (drawer?.classList.contains('active')) window.trapFocus?.(drawer, infoModalTrigger || drawer);
    });
  }

  function refreshPlacements() {
    bindSdkEvents();
    // A third-party widget must not interrupt basket rendering if it fails.
    try { window.Klarna?.OnsiteMessaging?.refresh?.(); } catch (_) {}
  }

  function ensureSdk(panel) {
    bindSdkEvents();
    if (sdkRequested) return;
    const existing = document.querySelector('script[src*="klarnacdn.net"][data-client-id], script[src*="klarna.com/web-sdk/"]');
    if (existing) {
      sdkRequested = true;
      existing.addEventListener('load', refreshPlacements, { once: true });
      return;
    }
    if (window.Klarna?.OnsiteMessaging) {
      sdkRequested = true;
      return;
    }
    if (!panel.dataset.clientId) return;
    sdkRequested = true;
    const script = document.createElement('script');
    script.src = 'https://js.klarna.com/web-sdk/v1/klarna.js';
    script.async = true;
    script.dataset.clientId = panel.dataset.clientId;
    script.dataset.environment = 'production';
    script.addEventListener('load', refreshPlacements, { once: true });
    document.head.appendChild(script);
  }

  function captureExposure() {
    if (exposureCaptured || !cohort || !window.posthog) return;
    // The marker is outside the hidden treatment so control uses the same point.
    const exposed = panels().find(panel => eligibleForKlarna(panel) && placementOpen(panel) &&
      visibleTargets.has(targets.get(panel)));
    if (!exposed) return;
    exposureCaptured = true;
    window.posthog.capture('experiment_exposure', {
      experiment: flag,
      variant: cohort,
      placement: exposed.dataset.context
    });
  }

  function update() {
    let refresh = false;
    panels().forEach(panel => {
      const hidden = cohort !== 'test' || !relevant(panel);
      if (panel.hidden !== hidden) {
        panel.hidden = hidden;
        refresh = true;
      }
      const wrapper = panel.querySelector('[data-itch-bnpl-klarna]');
      if (!wrapper) return;
      const amount = Number(panel.dataset.amount);
      const hideKlarna = hidden || !eligibleForKlarna(panel);
      if (wrapper.hidden !== hideKlarna) {
        wrapper.hidden = hideKlarna;
        refresh = true;
      }
      let placement = wrapper.querySelector('klarna-placement');
      // Hidden elements are still validated by Klarna. Keep ineligible placements
      // out of the document, and set the required amount before mounting one.
      if (hideKlarna) {
        if (placement) {
          placement.remove();
          refresh = true;
        }
        return;
      }
      if (!placement) {
        placement = wrapper.querySelector('template').content.firstElementChild.cloneNode(true);
        placement.dataset.purchaseAmount = String(amount);
        wrapper.appendChild(placement);
        refresh = true;
      } else if (placement.dataset.purchaseAmount !== String(amount)) {
        placement.dataset.purchaseAmount = String(amount);
        refresh = true;
      }
      ensureSdk(panel);
    });
    if (refresh) refreshPlacements();
    captureExposure();
  }

  function updateProductPrices(price = unitPrice, productId = unitProductId) {
    unitPrice = price;
    unitProductId = productId;
    if (Number.isSafeInteger(price) && price > 0) {
      panels().filter(panel => panel.dataset.context !== 'cart' &&
        (!productId || panel.dataset.productId === String(productId))).forEach(panel => {
        const container = panel.dataset.context === 'modal'
          ? panel.closest('.pcd-modal-overlay') : panel.closest('section');
        const quantity = Number(panel.dataset.context === 'modal'
          ? container?.querySelector('input[name="quantity"]:checked')?.value || 1
          : container?.querySelector('select[name="quantity"]')?.value || 1);
        const discount = window.getCartDiscountForQuantity?.(quantity);
        const discountedUnitPrice = Math.ceil(price * (discount ? 1 - discount.value : 1));
        const volumeModifier = quantity > 1 && (!discount || discount.volumeDiscountCombine) ? 0.9 : 1;
        panel.dataset.amount = String(Math.ceil(discountedUnitPrice * volumeModifier * quantity));
      });
    }
    update();
  }

  function scan() {
    panels().forEach(panel => {
      if (targets.has(panel)) return;
      const target = panel.previousElementSibling;
      if (!target?.hasAttribute('data-itch-bnpl-exposure')) return;
      targets.set(panel, target);
      observer?.observe(target);
    });
    updateProductPrices(window.pcd_current_unit_price_pence || unitPrice,
      window.pcd_current_unit_price_product_id || unitProductId);
  }

  window.pcdItchBnpl = {
    isInfoModalOpen: () => infoModalOpen,
    shouldKeepCartOpen: () => infoModalOpen || infoModalClosing,
    setCohort(value) {
      cohort = value === 'test' ? 'test' : value === 'control' ? 'control' : undefined;
      update();
    },
    updateProductPrices,
    updateCart(value) {
      cart = value;
      panels().filter(panel => panel.dataset.context === 'cart').forEach(panel => {
        panel.dataset.amount = String(cart.total_price);
      });
      update();
    },
    refresh: update
  };

  window.customElements?.whenDefined('klarna-placement').then(refreshPlacements);

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visibleTargets.add(entry.target);
        else visibleTargets.delete(entry.target);
      });
      captureExposure();
    }, { threshold: 0 });
  }
  const readFlag = () => window.pcdItchBnpl.setCohort(window.posthog?.getFeatureFlag(flag));
  if (window.pcdAnalytics) window.pcdAnalytics.onFeatureFlags(readFlag);
  else window.posthog?.onFeatureFlags(readFlag);
  document.addEventListener('change', () => updateProductPrices());
  document.addEventListener('shopify:section:load', scan);
  if ('MutationObserver' in window) {
    new MutationObserver(entries => {
      if (entries.some(entry => Array.from(entry.addedNodes).some(node => node.nodeType === 1 &&
        (node.matches('[data-itch-bnpl]') || node.querySelector('[data-itch-bnpl]'))))) scan();
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
